import { useState, useEffect, useCallback, useMemo } from "react";
import { apiFetch } from "../../../context/AuthContext";

const normalizeFeatureValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).toLowerCase());
  }

  if (typeof value === "object" && value !== null) {
    return Object.entries(value).reduce((acc, [key, val]) => {
      if (val) acc.push(String(key).toLowerCase());
      return acc;
    }, []);
  }

  if (typeof value === "string") {
    return [value.toLowerCase()];
  }

  return [];
};

const mapFeatureAliases = {
  view: ["view", "viewattendance", "viewattendancehistory", "view_history", "viewattendance_history"],
  refresh: ["refresh"],
  export_csv: ["export_csv", "exportcsv", "exportcsv"],
  export_template: ["export_template", "exporttemplate", "export_template"],
  import_csv: ["import_csv", "importcsv", "import.csv"],
  schedule_template: ["schedule_template", "scheduletemplate", "schedule_template"],
  default_location: ["default_location", "defaultlocation", "default_location"],
  import_location_schedule: ["import_location_schedule", "importlocationschedule", "import_location_schedule"],
  manage_shifts: ["manage_shifts", "manageshifts"],
  assign_shifts: ["assign_shifts", "assignshifts"],
  remote_punch: ["remote_punch", "remotepunch"],
  reset_check_in: ["reset_check_in", "resetcheckin", "reset_checkin"],
  reset_check_out: ["reset_check_out", "resetcheckout", "reset_checkout"],
  revert_original: ["revert_original", "revertoriginal", "revert_to_original", "reverttooriginal"],
  approve_overtime: ["approve_overtime", "approveovertime", "approve-overtime", "overtime", "overtimeapproval"],
};

const resolveFeatureAliases = (featureName) => {
  const normalized = String(featureName || "").toLowerCase();
  const [, permissionPart] = normalized.split(".");

  if (permissionPart) {
    return mapFeatureAliases[permissionPart] || [permissionPart];
  }

  return mapFeatureAliases[normalized] || [normalized];
};

const usePermissions = ({ isAdminView, profile, BASE_URL, language }) => {
  const [permissions, setPermissions] = useState({ modules: [], features: {} });
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [userRole, setUserRole] = useState("");

  const workingCompany =
    profile?.workingCompany ||
    profile?.clientName ||
    localStorage.getItem("workingCompany") ||
    localStorage.getItem("clientName") ||
    localStorage.getItem("client") ||
    "";

  const apiBaseUrl = BASE_URL || process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_URL || "";
  const resolvedApiBaseUrl = apiBaseUrl.endsWith("/api") ? apiBaseUrl : `${apiBaseUrl.replace(/\/+$/, "")}/api`;

  const fetchPermissions = useCallback(async () => {
    try {
      setPermissionsLoading(true);
      if (!workingCompany) {
        setPermissions({ modules: [], features: {} });
        return;
      }

      const response = await apiFetch(
        `${resolvedApiBaseUrl}/admin-levels/resolve/permissions?workingCompany=${encodeURIComponent(workingCompany)}`,
        { method: "GET" }
      );
      const data = await response.json();
      setPermissions(data?.permissions || {});
    } catch (err) {
      console.error("Failed to fetch permissions:", err);
      setPermissions({ modules: [], features: {} });
    } finally {
      setPermissionsLoading(false);
    }
  }, [resolvedApiBaseUrl, workingCompany]);

  useEffect(() => {
    setUserRole(profile?.role || localStorage.getItem("role") || "");
    // Always fetch permissions for the current working company so UI and
    // action gating match backend behavior regardless of view type.
    fetchPermissions();
  }, [isAdminView, profile, fetchPermissions]);

  const attendancePermissions = useMemo(() => {
    const raw =
      permissions?.features?.attendance ||
      permissions?.features?.attendanceManagement ||
      permissions?.features?.attendance_management ||
      permissions?.features?.attendancemanagement ||
      permissions?.features?.attendance?.features ||
      [];
    return normalizeFeatureValue(raw);
  }, [permissions]);

  const isAdminRole = useMemo(
    () => userRole === "admin" || userRole === "superadmin",
    [userRole]
  );

  const hasPermission = useCallback(
    (featureName) => {
      // Super-admins always have permission.
      if (isAdminRole) return true;
      // Resolver bypass flags (admin/superadmin responses carry these even
      // when the local role value hasn't loaded yet).
      if (permissions?.allModulesAllowed || permissions?.isSuperAdmin) return true;

      const featureKeys = resolveFeatureAliases(featureName);
      return featureKeys.some((key) => attendancePermissions.includes(key));
    },
    [attendancePermissions, isAdminRole, permissions]
  );

  const can = useCallback((featureName) => hasPermission(featureName), [hasPermission]);
  const canAny = useCallback((...featureNames) => featureNames.some((name) => hasPermission(name)), [hasPermission]);
  const canAll = useCallback((...featureNames) => featureNames.every((name) => hasPermission(name)), [hasPermission]);

  return { permissions, permissionsLoading, userRole, can, canAny, canAll, hasPermission };
};

export default usePermissions;